// src/components/SearchInput.jsx
import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Upload, X, AlertTriangle, ShieldCheck, CornerDownLeft } from 'lucide-react';
import { searchTypeConfig } from '../config/searchTypeConfig';

// Platforms excluded from email scanning due to OTP side-effects
const EMAIL_OTP_DISCLAIMER = `This email scan only checks platforms with safe, public registration endpoints.
Platforms that detect accounts by triggering password-reset or OTP flows (e.g. Flipkart, Instagram, Snapchat)
are excluded to prevent sending unsolicited emails to the target address.`;

const validateEmail = (email) => {
  if (!email) return null; // empty — no state
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? 'valid' : 'invalid';
};

const OtpDisclaimerModal = ({ onAccept, onCancel }) => (
  <div className="modal-overlay" onClick={onCancel}>
    <div
      className="modal-box glass"
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 12, flexShrink: 0,
          background: 'rgba(245,158,11,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <AlertTriangle size={20} color="#fbbf24" />
        </div>
        <div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Email Scan Notice</h3>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-muted)' }}>Before you proceed</p>
        </div>
      </div>

      <div style={{
        padding: '14px 16px',
        background: 'rgba(245,158,11,0.07)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid rgba(245,158,11,0.18)',
        marginBottom: 20,
      }}>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.65, color: 'var(--color-text-secondary)' }}>
          {EMAIL_OTP_DISCLAIMER}
        </p>
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24,
        padding: '10px 14px',
        background: 'rgba(16,185,129,0.07)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid rgba(16,185,129,0.18)',
      }}>
        <ShieldCheck size={14} color="#34d399" />
        <span style={{ fontSize: 12, color: '#34d399' }}>
          OTP-triggering platforms are automatically excluded from your results.
        </span>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onCancel} className="btn btn-ghost" style={{ flex: 1 }}>
          Cancel
        </button>
        <button onClick={onAccept} className="btn btn-primary" style={{ flex: 2 }}>
          <ShieldCheck size={14} /> Understood — Proceed
        </button>
      </div>
    </div>
  </div>
);

const SearchInput = ({ searchType, onBack, onSearch }) => {
  const [input, setInput] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [emailValidation, setEmailValidation] = useState(null); // null | 'valid' | 'invalid'
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const config = searchTypeConfig[searchType];

  // Auto-focus the input on mount
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  // Validate email in real-time
  useEffect(() => {
    if (searchType === 'email') {
      setEmailValidation(validateEmail(input));
    } else {
      setEmailValidation(null);
    }
  }, [input, searchType]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
    setInput(file.name);
  };

  const clearFile = () => {
    setSelectedFile(null);
    setPreview(null);
    setInput('');
  };

  const handleSubmit = () => {
    if (searchType === 'image' && selectedFile) {
      onSearch(selectedFile);
      return;
    }
    if (!input.trim()) return;

    // Show disclaimer before email searches
    if (searchType === 'email') {
      setShowDisclaimer(true);
      return;
    }
    onSearch(input.trim());
  };

  const handleEmailAccept = () => {
    setShowDisclaimer(false);
    onSearch(input.trim());
  };

  const inputClass = [
    'input-field',
    emailValidation === 'valid'   ? 'input-valid'   : '',
    emailValidation === 'invalid' ? 'input-invalid' : '',
  ].filter(Boolean).join(' ');

  const canSubmit = searchType === 'image'
    ? (selectedFile || input.trim())
    : (input.trim() && emailValidation !== 'invalid');

  return (
    <>
      {showDisclaimer && (
        <OtpDisclaimerModal
          onAccept={handleEmailAccept}
          onCancel={() => setShowDisclaimer(false)}
        />
      )}

      <div className="animate-fade-in" style={{ width: '100%', maxWidth: 720 }}>
        <div className="glass" style={{ borderRadius: 'var(--radius-xl)', padding: '40px 40px 36px' }}>

          {/* Back */}
          <button onClick={onBack} className="btn btn-ghost btn-sm" style={{ marginBottom: 28 }}>
            <ArrowLeft size={14} /> Back
          </button>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 52, marginBottom: 14, lineHeight: 1 }}>{config.icon}</div>
            <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em' }}>
              {config.title}
            </h2>
            <p style={{ margin: '8px 0 0', color: 'var(--color-text-secondary)', fontSize: 14 }}>
              {config.description}
            </p>
          </div>

          {/* Image upload */}
          {searchType === 'image' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {!preview ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    border: '2px dashed var(--color-border)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '48px 24px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 200ms',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-accent)';
                    e.currentTarget.style.background = 'rgba(99,102,241,0.04)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-border)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Upload size={36} color="var(--color-text-muted)" style={{ marginBottom: 12 }} />
                  <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 14 }}>
                    Drop image here or{' '}
                    <span style={{ color: 'var(--color-accent)', fontWeight: 600 }}>browse</span>
                  </p>
                  <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--color-text-muted)' }}>
                    PNG, JPG, WEBP — EXIF metadata will be extracted
                  </p>
                  <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*" style={{ display: 'none' }} />
                </div>
              ) : (
                <div style={{ position: 'relative', borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--color-border)', background: 'rgba(0,0,0,0.4)', maxHeight: 240 }}>
                  <img src={preview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                  <button
                    onClick={clearFile}
                    style={{
                      position: 'absolute', top: 10, right: 10,
                      width: 30, height: 30, borderRadius: '50%',
                      background: 'rgba(244,63,94,0.9)',
                      border: 'none', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <X size={14} color="white" />
                  </button>
                </div>
              )}

              <div style={{ position: 'relative' }}>
                <input
                  className="input-field"
                  type="text"
                  value={input}
                  onChange={(e) => { setInput(e.target.value); setSelectedFile(null); setPreview(null); }}
                  placeholder="Or paste image URL…"
                  style={{ paddingRight: 120 }}
                />
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className="btn btn-primary"
                  style={{ position: 'absolute', right: 6, top: 6, bottom: 6 }}
                >
                  Analyze
                </button>
              </div>
            </div>
          ) : (
            /* Standard input */
            <div>
              <div style={{ position: 'relative' }}>
                <input
                  ref={inputRef}
                  className={inputClass}
                  type={searchType === 'email' ? 'email' : 'text'}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && canSubmit && handleSubmit()}
                  placeholder={config.placeholder}
                  style={{ paddingRight: 120 }}
                  autoComplete="off"
                  spellCheck={false}
                />
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className="btn btn-primary"
                  style={{ position: 'absolute', right: 6, top: 6, bottom: 6 }}
                >
                  Search
                </button>
              </div>

              {/* Email validation hint */}
              {searchType === 'email' && input && (
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {emailValidation === 'valid' ? (
                    <>
                      <ShieldCheck size={12} color="var(--color-emerald)" />
                      <span style={{ fontSize: 11, color: 'var(--color-emerald)' }}>Valid email format</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle size={12} color="var(--color-rose)" />
                      <span style={{ fontSize: 11, color: 'var(--color-rose)' }}>Enter a valid email address</span>
                    </>
                  )}
                </div>
              )}

              {/* Enter hint */}
              {input && emailValidation !== 'invalid' && (
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 4, color: 'var(--color-text-muted)', fontSize: 11 }}>
                  <CornerDownLeft size={11} />
                  Press Enter to search
                </div>
              )}

              {/* Examples */}
              <div style={{ marginTop: 22, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Try:</span>
                {config.examples.map((ex) => (
                  <span
                    key={ex}
                    onClick={() => setInput(ex)}
                    style={{
                      padding: '4px 10px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-full)',
                      fontSize: 11,
                      cursor: 'pointer',
                      fontFamily: 'var(--font-mono)',
                      transition: 'all 150ms',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.10)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                  >
                    {ex}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchInput;
